/**
 * Sets the document title for all <title> elements.
 */
export function setDocumentTitle(title: string): void {
  Array.from(document.getElementsByTagName('title')).forEach(
    (elem) => (elem.innerHTML = title),
  );
}

/**
 * Replaces the browser favicon by removing all existing <link rel="icon">
 * elements and appending a new one with a cache-busting timestamp.
 * Mutating href alone is ignored by aggressive favicon caches (Chrome, Safari),
 * so a full remove-and-recreate is required.
 */
export function setDocumentFavicon(url: string): void {
  document
    .querySelectorAll("link[rel*='icon']")
    .forEach((el) => el.parentElement?.removeChild(el));

  const link = document.createElement('link');
  link.rel = 'icon';

  const imageType = getFaviconImageType(url);
  if (imageType) link.type = imageType;

  if (isPresignedS3Url(url)) {
    link.href = url;
    document.head.appendChild(link);
    return;
  }

  const sep = url.includes('?') ? '&' : '?';
  link.href = `${url}${sep}t=${Date.now()}`;
  document.head.appendChild(link);
}

function isPresignedS3Url(url: string): boolean {
  return url.includes('X-Amz-Signature=') || url.includes('X-Amz-Credential=');
}

function getFaviconImageType(url: string): string | undefined {
  const dataUriMatch = url.match(/^data:([^;,]+)/i);
  if (dataUriMatch?.[1]) return dataUriMatch[1];

  const pathname = getUrlPathname(url).toLowerCase();
  if (pathname.endsWith('.svg')) return 'image/svg+xml';
  if (pathname.endsWith('.png')) return 'image/png';
  if (pathname.endsWith('.jpg') || pathname.endsWith('.jpeg'))
    return 'image/jpeg';
  if (pathname.endsWith('.webp')) return 'image/webp';
  if (pathname.endsWith('.gif')) return 'image/gif';
  if (pathname.endsWith('.ico')) return 'image/x-icon';
  return undefined;
}

function getUrlPathname(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
}
